// src/components/ResetPassword.js
import React, { useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { PuffLoader } from "react-spinners";
import API_ENDPOINTS from "../config/api";

const ResetPassword = () => {
  const [crNumber, setCrNumber] = useState(""); // CRnumber input
  const [step, setStep] = useState(1);         // step 1: enter CRnumber, step 2: set new password
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [obscureNew, setObscureNew] = useState(true);
  const [obscureConfirm, setObscureConfirm] = useState(true);

  const navigate = useNavigate();

  // Step 1: Check if user (CRnumber) exists
  const handleCheckUser = async () => {
    // Basic validation: Must not be empty
    if (!crNumber) {
      toast.warn("Please enter your CRnumber.");
      return;
    }
    // Additional check: Must start with CR in uppercase
    if (!crNumber.startsWith("CR")) {
      toast.warn("Your CRnumber must start with 'CR' in uppercase (e.g., CR1234).");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(API_ENDPOINTS.CHECK_USER, {
        account: crNumber,
      });
      setLoading(false);

      // If user is found, move to next step
      if (response.status === 200 && response.data.found) {
        setStep(2);
        toast.success("User found. Please set your new password.");
      } else {
        toast.error("User not found or some error occurred.");
      }
    } catch (error) {
      setLoading(false);
      console.error("Error checking user existence:", error);
      toast.error("User Not Found check your correct CRNumber on Deriv.");
    }
  };

  // Step 2: Submit new password
  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.warn("Please fill in all password fields.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    // Validate password format on frontend (optional)
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*_\-=[\]{};:,.<>?])[A-Za-z\d!@#$%^&*_\-=[\]{};:,.<>?]{8,}$/;
    if (!passwordRegex.test(newPassword)) {
      toast.warn(
        "Password must have at least 8 characters, an uppercase, a lowercase, a number, and a special character."
      );
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(API_ENDPOINTS.RESET_PASSWORD, {
        account: crNumber,
        newPassword,
      });
      setLoading(false);

      if (response.status === 200) {
        toast.success("Password reset successful! Please sign in.");
        navigate("/signin");
      } else {
        toast.error("Failed to reset password. Please try again.");
      }
    } catch (error) {
      setLoading(false);
      console.error("Error resetting password:", error);
      toast.error("Error resetting password. Please try again.");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

      {loading ? (
        <div className="flex justify-center items-center min-h-screen">
          <PuffLoader color="#008080" size={50} />
        </div>
      ) : (
        <div className="w-full max-w-md bg-white rounded-lg shadow-md p-6 text-center">
          {step === 1 && (
            <>
              <h1 className="text-2xl font-bold mb-4">Reset Password</h1>
              <p className="mb-6">Enter your CRnumber to continue.</p>
              <input
                type="text"
                placeholder="CRnumber"
                className="w-full p-3 mb-4 border rounded"
                value={crNumber}
                onChange={(e) => {
                  // Force uppercase and remove all spaces
                  const cleaned = e.target.value.toUpperCase().replace(/\s+/g, "");
                  setCrNumber(cleaned);
                }}
              />
              <button
                onClick={handleCheckUser}
                className="w-full py-3 bg-teal-600 text-white rounded-md text-lg font-semibold hover:bg-teal-700 transition"
              >
                Check User
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <h1 className="text-2xl font-bold mb-4">Set New Password</h1>
              <div className="mb-4 relative">
                <input
                  type={obscureNew ? "password" : "text"}
                  placeholder="New Password"
                  className="w-full p-3 border rounded"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setObscureNew(!obscureNew)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm text-gray-700"
                >
                  {obscureNew ? "Show" : "Hide"}
                </button>
              </div>
              <div className="mb-4 relative">
                <input
                  type={obscureConfirm ? "password" : "text"}
                  placeholder="Confirm Password"
                  className="w-full p-3 border rounded"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setObscureConfirm(!obscureConfirm)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm text-gray-700"
                >
                  {obscureConfirm ? "Show" : "Hide"}
                </button>
              </div>
              <button
                onClick={handleResetPassword}
                className="w-full py-3 bg-teal-600 text-white rounded-md text-lg font-semibold hover:bg-teal-700 transition"
              >
                Reset Password
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ResetPassword;
